// React Dependencies
import React, { useState, useRef, useEffect } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import MaterialTable from 'material-table';
import { Box, FormControl, TextField, InputLabel, Select, MenuItem } from '@material-ui/core';

import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';

// Internal Dependencies
import { getPaginatePages } from '../../actions/admin/pageAction';
import useDebounce from '../global/use-debounce';

import { IsEmpty } from '../../common/checks';
import Constants from '../../common/constants';

const initializeFocusValue = {
  idFocus:   false,
  nameFocus: false,
};

const MapTable = (props) => {
  const table = useRef(null);

  const [currentOrder, setCurrentOrder] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [autoFocus, setAutoFocus] = useState(initializeFocusValue);

  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const [mallMapStatus, setMallMapStatus] = useState('');

  const debounceId = useDebounce(id, 500);
  const debouncedName = useDebounce(name, 500);

  const { idFocus, nameFocus } = autoFocus;

  const columns = [
    { title: 'ID', field: 'id', defaultSort: 'asc' },
    {
      title:         'Name',
      field:         'name',
      defaultFilter: 'name',
      render:        (page) => (
        <Link target="_blank" to={`/admin/page/${page.id}`}>
          {page.name}
        </Link>
      ),
    },
    { title: 'Page Status', field: 'status' },
    { title: 'Mall Map Status', field: 'mallMapStatus' },
  ];

  useEffect(() => {
    if (table.current) table.current.onQueryChange();
  }, [debounceId, debouncedName, status, mallMapStatus]);

  function handleSort() {
    if (currentOrder) {
      if (currentOrder === 'DESC') setCurrentOrder('ASC');
      else setCurrentOrder('DESC');
    } else {
      setCurrentOrder('DESC');
    }
  }

  const handleIdChange = (e) => {
    setId(e.target.value);
    setAutoFocus({ idFocus: true, nameFocus: false });
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    setAutoFocus({ idFocus: false, nameFocus: true });
  };

  return (
    <MaterialTable
      tableRef={table}
      className="material-table"
      title="Map"
      columns={columns}
      onChangeRowsPerPage={(size) => {
        setCurrentOrder('');
        setAutoFocus(initializeFocusValue);
        setPageSize(size);
      }}
      icons={{
        SortArrow: () => (currentOrder === '' ? <UnfoldMoreIcon onClick={handleSort} />
          : currentOrder === 'ASC' ? <ArrowUpwardIcon onClick={handleSort} />
            : <ArrowDownwardIcon onClick={handleSort} />),
      }}
      data={(query) => new Promise(async (resolve) => {
        try {
          query.id = id;
          query.name = name;
          query.status = status;
          query.mallMapStatus = mallMapStatus;
          query.types = [Constants.PageType.Mall];

          if (!IsEmpty(query.orderBy)) {
            query.order = { col: query.orderBy.field, dir: currentOrder };
          }
          /**
           * The MaterialTable has an issue,
           * if press sort 3x, the 3rd value of orderBy will be undefined, so for preventing that one, in this condition we only set the dir value.
           */
          if (!IsEmpty(query.order)) {
            query.order.dir = currentOrder;
          }
          const result = await props.getPaginatePages(query);

          let { rows, count } = result;
          let { page } = query;

          if (rows.length === 0) {
            page = 0;

            if (count !== 0) {
              const resultFirstPage = await props.getPaginatePages({ ...query, page });
              rows = resultFirstPage.rows;
            }
          }

          resolve({ data: rows, page, totalCount: count });
        } catch (err) {
          resolve({ data: [], page: 0, totalCount: 0 });
        }
      })}
      actions={[
        {
          icon:    'visibility',
          tooltip: 'View Mall',
          onClick: (event, page) => {
            window.open(`/admin/mallMaps/${page.id}/mallNavigation`, '_blank');
          },
        },
      ]}
      components={{
        Toolbar: () => (
          <div>
            <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
              <Box p={1} width="10%">
                <FormControl fullWidth>
                  <TextField
                    label="Id"
                    type="number"
                    value={id}
                    onChange={handleIdChange}
                    autoFocus={idFocus}
                  />
                </FormControl>
              </Box>

              <Box p={1} width="23%">
                <FormControl fullWidth>
                  <TextField
                    label="Page Name"
                    id="standard-adornment-amount"
                    placeholder="Search by name"
                    value={name}
                    onChange={handleNameChange}
                    type="search"
                    autoFocus={nameFocus}
                  />
                </FormControl>
              </Box>

              <Box p={1} width="20%">
                <FormControl fullWidth>
                  <InputLabel id="status-filter-label">Page Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    name="pageStatus"
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={Constants.Status.Pending}>Pending</MenuItem>
                    <MenuItem value={Constants.Status.Live}>Live</MenuItem>
                    <MenuItem value={Constants.Status.Rejected}>Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box p={1} width="20%">
                <FormControl fullWidth>
                  <InputLabel id="mall-map-status-filter-label">Mall Map Status</InputLabel>
                  <Select
                    labelId="mall-map-status-filter-label"
                    name="pageMallMapStatus"
                    value={mallMapStatus}
                    onChange={(e) => { setMallMapStatus(e.target.value); }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={Constants.Status.Pending}>Pending</MenuItem>
                    <MenuItem value={Constants.Status.Draft}>Draft</MenuItem>
                    <MenuItem value={Constants.Status.Live}>Live</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </div>
        ),
      }}
      options={{
        actionsColumnIndex:  -1,
        paging:              true,
        emptyRowsWhenPaging: false,
        filtering:           false,
        search:              false,
        pageSize,
      }}
    />
  );
};

MapTable.propTypes = {
  getPaginatePages: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  store: state.admin.page,
});

export default connect(mapStateToProps, {
  getPaginatePages,
})(MapTable);
